import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'wouter';

import { isProjectOverviewPath } from '@/lib/chatLayout';

export const OVERVIEW_CHAT_ANCHOR_VAR = '--overview-chat-anchor-left';

type OverviewBentoExpandContextValue = {
  expanded: boolean;
  toggleExpanded: () => void;
  setExpanded: (next: boolean) => void;
};

const OverviewBentoExpandContext =
  createContext<OverviewBentoExpandContextValue | null>(null);

export function OverviewBentoExpandProvider({ children }: { children: ReactNode }) {
  const [pathname] = useLocation();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const p = pathname === '' ? '/' : pathname;
    if (!isProjectOverviewPath(p)) setExpanded(false);
  }, [pathname]);

  const toggleExpanded = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const value = useMemo(
    (): OverviewBentoExpandContextValue => ({
      expanded,
      toggleExpanded,
      setExpanded,
    }),
    [expanded, toggleExpanded],
  );

  return (
    <OverviewBentoExpandContext.Provider value={value}>
      {children}
    </OverviewBentoExpandContext.Provider>
  );
}

export function useOverviewBentoExpand(): OverviewBentoExpandContextValue {
  const ctx = useContext(OverviewBentoExpandContext);
  if (!ctx) {
    throw new Error(
      'useOverviewBentoExpand must be used within OverviewBentoExpandProvider',
    );
  }
  return ctx;
}
